/**
 * Подпись «где общались» с отметкой, кто из сотрудников сейчас на этом нашем канале (владелец,
 * 08.10.26): зелёная точка и имя — на канале кто-то есть, красная — никого. Судим по двум вещам
 * (`online.kind`): у номера из браузера — по отметке вкладки расширения за 15 минут (`tab`); у
 * каналов, где отвечают из «Чатов» (Wazzup, боты Telegram и MAX, Авито, ВК, Instagram), — по
 * ответу сотрудника за 20 минут в любой из них (`reply`): отвечают на них с одного экрана. Клик по
 * подписи — окно: у номера его WhatsApp, Telegram и MAX тем же выводом, что «Расширение» → «Кто
 * онлайн» → «По номерам»; у канала — кто отвечает из «Чатов» и где именно ответил (ссылки на чаты).
 */
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";

import { ChannelCell, Dot, ago, when } from "@/components/ext/PhoneChannel";
import {
  fmtAccountPhone,
  loadStaffReplies,
  type AccountOnline,
  type AccountOnlineUser,
  type Channel,
  type ChatBrief,
  type StaffReply,
} from "@/lib/clientChats";
import { loadExtStatus } from "@/lib/extension";

const PHONES = "/extension?tab=who&by=phones";
const WIDTH = 360;

/** «Анурова Ульяна» → «Анурова У.» — в строке списка места мало. */
function short(name: string): string {
  const [first, second] = name.trim().split(/\s+/);
  return second ? `${first} ${second[0]}.` : (first ?? "");
}

const who = (u: { id: number; name: string }) => u.name || `#${u.id}`;

/** Сколько секунд назад была отметка: `seen` — часы сервера (MSK, без пояса). Сервер `age` больше не
 * шлёт: он менялся каждым снимком и перерисовывал весь список «Чатов» (аудит 21.10.26). */
const ageOf = (u: { seen: string; age?: number }) =>
  u.age ?? Math.max(0, Math.round((Date.now() - Date.parse(`${u.seen}+03:00`)) / 1000));

/** Слова под то, по чему судим: вкладка расширения на номере или ответ из «Чатов» в канале. */
const WORDS = {
  tab: {
    now: "На номере сейчас",
    off: "не онлайн",
    idle: "Не онлайн: за 15 минут номер никто не держал. Последняя отметка",
    never: "Не онлайн: этот номер в браузере с расширением никто не открывал.",
    tail: " Нажмите — все каналы номера.",
  },
  reply: {
    now: "Отвечают из «Чатов» сейчас",
    off: "никто не отвечает",
    idle: "За 20 минут из «Чатов» никто не отвечал. Последний ответ",
    never: "За 7 дней из «Чатов» никто не отвечал.",
    tail: " Нажмите — подробнее.",
  },
} as const;

function hint(o: AccountOnline): string {
  const w = WORDS[o.kind];
  if (o.active) return `${w.now}: ${o.users.map((u) => `${who(u)} (${ago(ageOf(u))})`).join(", ")}.${w.tail}`;
  if (o.last) return `${w.idle} — ${who(o.last)}, ${ago(ageOf(o.last))}.${w.tail}`;
  return `${w.never}${w.tail}`;
}

function Mark({ online, compact, inverse }: { online: AccountOnline; compact: boolean; inverse: boolean }) {
  const dot = `inline-block size-2 flex-none rounded-full ${online.active ? "bg-ok" : "bg-err"} ${inverse ? "ring-1 ring-white" : ""}`;
  const [first, ...rest] = online.users;
  const off = WORDS[online.kind].off;
  const names = compact
    ? first && `${short(who(first))}${rest.length ? ` +${rest.length}` : ""}`
    : online.active
      ? online.users.map((u) => short(who(u))).join(", ") // и в шапке чата коротко — полностью в окне по клику
      : online.last
        ? `${off} · последний: ${short(who(online.last))}, ${ago(ageOf(online.last))}`
        : off;
  const tone = inverse ? "" : online.active ? "text-ok" : compact ? "" : "text-err";
  return (
    <span className={`inline-flex flex-none items-center gap-1 ${tone}`} data-annot="кто на канале">
      <span className={dot} />
      {names && <span className={compact ? "max-w-[96px] truncate" : ""}>{names}</span>}
    </span>
  );
}

/** Окно у подписи: живёт в `body`, закрывается кликом мимо и Escape. Его же открывает полоска
 * «Ждут ответа» внизу списка «Чатов» (`label` — как окно названо для чтения с экрана). */
export function Card({
  anchor,
  close,
  title,
  sub,
  foot,
  label = "Кто работает на канале",
  children,
}: {
  /** Подпись, от которой открыто окно: клик по ней закрывает его сам. */
  anchor: HTMLElement;
  close: () => void;
  title: string;
  sub?: string;
  foot: ReactNode;
  label?: string;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [at] = useState(() => anchor.getBoundingClientRect());
  useEffect(() => {
    const away = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!box.current?.contains(target) && !anchor.contains(target)) close();
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [anchor, close]);

  const width = Math.min(WIDTH, window.innerWidth - 16);
  const left = Math.max(8, Math.min(at.left, window.innerWidth - width - 8));
  // Внизу экрана окно открываем вверх от подписи.
  const up = at.bottom + 300 > window.innerHeight && at.top > 300;
  const place = up ? { bottom: window.innerHeight - at.top + 6 } : { top: at.bottom + 6 };

  return createPortal(
    // Окно живёт в `body`, но события React всплывают к строке чата (ссылке) — гасим их здесь.
    <div
      ref={box}
      role="dialog"
      aria-label={label}
      onClick={(e) => e.stopPropagation()}
      style={{ left, width, ...place }}
      className="fixed z-50 flex flex-col gap-2.5 rounded-[var(--radius-card)] border border-border bg-bg p-3 text-[12.5px] leading-snug text-fg shadow-[0_8px_30px_rgba(0,0,0,.22)]"
      data-annot="окно канала"
    >
      <div className="flex items-baseline gap-2">
        <span className="text-[13.5px] font-semibold">{title}</span>
        {sub && <span className="tabular-nums text-fg-muted">{sub}</span>}
        <button type="button" onClick={close} title="Закрыть" className="ml-auto self-center text-fg-muted hover:text-fg">
          <X size={15} />
        </button>
      </div>
      {children}
      <div className="flex items-baseline justify-between gap-3 text-fg-faint">{foot}</div>
    </div>,
    document.body,
  );
}

/** Номер из браузера: три его канала тем же выводом, что в разрезе «По номерам». */
function PhoneCard({ chat, online, anchor, close }: { chat: ChatBrief; online: AccountOnline; anchor: HTMLElement; close: () => void }) {
  const q = useQuery({ queryKey: ["ext-status"], queryFn: loadExtStatus, refetchInterval: 15_000 });
  const { name, phone } = chat.account;
  const row = q.data?.phones.find((p) => p.phone === phone);
  return (
    <Card
      anchor={anchor}
      close={close}
      title={name || row?.name || "без названия"}
      sub={fmtAccountPhone(phone)}
      foot={
        <>
          <span title="В списке чатов «онлайн» шире: на номере были за последние 15 минут">
            «сейчас» — отметка вкладки за {((q.data?.active_seconds ?? 150) / 60).toLocaleString("ru-RU")} мин
          </span>
          <Link to={PHONES} className="flex-none text-accent-text hover:underline">
            Все номера →
          </Link>
        </>
      }
    >
      {row ? (
        <div className="flex flex-col divide-y divide-border">
          {row.channels.map((c) => (
            <div key={c.channel} className="flex gap-3 py-2">
              <span className={`w-[68px] flex-none ${c.channel === chat.channel ? "font-semibold" : "text-fg-muted"}`}>{c.platform}</span>
              <ChannelCell c={c} />
            </div>
          ))}
        </div>
      ) : q.isPending ? (
        <span className="text-fg-muted">Загружаю…</span>
      ) : q.isError ? (
        <span className="text-err">Не удалось получить отметки расширения: {String(q.error.message)}</span>
      ) : (
        // Номера нет в справочнике CRM (личный аккаунт сотрудника) — показываем то, что знаем о чате.
        <span className="text-fg-muted">
          Номера нет в справочнике рабочих номеров CRM.{" "}
          {online.active
            ? `Сейчас на нём: ${online.users.map(who).join(", ")}.`
            : online.last
              ? `Не онлайн, последний: ${who(online.last)}, ${ago(ageOf(online.last))}.`
              : "В браузере с расширением его не открывали."}
        </span>
      )}
    </Card>
  );
}

/** Каналы, куда отвечают из «Чатов», — как их зовут во вкладках списка. */
const REPLY_TO: Record<Channel, string> = {
  whatsapp: "Wazzup",
  telegram: "бот Telegram",
  max: "бот MAX",
  avito: "Авито",
  vk: "ВКонтакте",
  instagram: "Instagram",
  livechat: "онлайн-чат сайта",
};

/** «Авито 2, бот Telegram 1» — куда сотрудник отвечал за 20 минут. */
function spread(u: AccountOnlineUser): string {
  const parts = Object.entries(u.channels ?? {}) as [Channel, number][];
  return parts
    .sort((a, b) => b[1] - a[1])
    .map(([channel, n]) => `${REPLY_TO[channel] ?? channel} ${n}`)
    .join(", ");
}

/** Где именно человек ответил: последние ответы ссылками на чаты (владелец 20.10.26: «кликнуть и
 * посмотреть, где именно она ответила»). */
function Replies({ list }: { list: StaffReply[] | null | undefined }) {
  if (list === null) return <span className="text-err">Не удалось загрузить, где отвечали</span>;
  if (!list) return <span className="text-fg-faint">Загружаю ответы…</span>;
  if (!list.length) return <span className="text-fg-faint">ответов за 7 дней нет</span>;
  return (
    <div className="mt-1 flex max-h-[180px] flex-col gap-1 overflow-y-auto">
      {list.map((r, i) => (
        <Link
          key={i}
          to={`/chats/${r.chat_id}`}
          className="flex flex-col rounded px-1.5 py-1 hover:bg-bg-hover"
          title="Открыть чат, где ответили"
        >
          <span className="flex items-baseline gap-1.5">
            <span className="min-w-0 truncate font-medium text-accent-text">{r.chat || `чат ${r.chat_id}`}</span>
            <span className="flex-none text-fg-muted">· {REPLY_TO[r.channel] ?? r.channel}</span>
            <span className="ml-auto flex-none tabular-nums text-fg-faint">{when(r.date)}</span>
          </span>
          <span className="truncate text-fg-muted">{r.text || (r.file ? "‹файл›" : "")}</span>
        </Link>
      ))}
    </div>
  );
}

/**
 * Каналы, где отвечают из «Чатов»: кто отвечал за последние 20 минут (в любой из них — экран
 * один) и чей ответ последний; под каждым — где именно он ответил, ссылками на чаты.
 */
function ReplyCard({ online, anchor, close }: { online: AccountOnline; anchor: HTMLElement; close: () => void }) {
  const where = (u: AccountOnlineUser) => (u.channel ? ` · ${REPLY_TO[u.channel] ?? u.channel}` : "");
  const ids = (online.active ? online.users : online.last ? [online.last] : []).map((u) => u.id);
  const replies = useQuery({
    queryKey: ["staff-replies", ids],
    queryFn: () => loadStaffReplies(ids),
    enabled: ids.length > 0,
  });
  const of = (u: AccountOnlineUser) => (replies.isError ? null : replies.data && (replies.data.users[String(u.id)] ?? []));
  return (
    <Card
      anchor={anchor}
      close={close}
      title="Кто отвечает из «Чатов»"
      foot={
        <span>
          Wazzup, боты Telegram и MAX, Авито, ВК и Instagram отвечаются с одного экрана: «на канале» — ответил в любой
          из них за последние 20 минут. Шаблоны рассылок и ответы ботов не в счёт.
        </span>
      }
    >
      {online.active ? (
        <div className="flex flex-col gap-1.5">
          {online.users.map((u) => (
            <div key={u.id} className="flex items-baseline gap-2">
              <Dot tone="ok" />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="font-medium">{who(u)}</span>
                <span className="text-fg-muted" title={when(u.seen)}>
                  последний ответ {ago(ageOf(u))}
                  {where(u)}
                </span>
                <span className="text-fg-muted">
                  ответов за 20 минут: {u.replies ?? 1}
                  {spread(u) && ` — ${spread(u)}`}
                </span>
                <Replies list={of(u)} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-baseline gap-2">
          <Dot tone={online.last ? "err" : "off"} />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-err">никто не отвечает</span>
            {online.last ? (
              <>
                <span className="text-fg-muted" title={when(online.last.seen)}>
                  последний ответ: {who(online.last)} · {ago(ageOf(online.last))}
                  {where(online.last)}
                </span>
                <Replies list={of(online.last)} />
              </>
            ) : (
              <span className="text-fg-faint">за 7 дней из «Чатов» не отвечали</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Подпись места переписки. `full` — оно же полностью («WhatsApp · «Wazzup» +7 925 …»): подсказка
 * и заголовок окна. `compact` — строка списка: имя сотрудника коротко; `inverse` — строка выбрана
 * (белым по синему). Сервер отметки не прислал — просто текст.
 */
export function AccountPlace({
  chat,
  full,
  compact = false,
  inverse = false,
  className = "",
  children,
}: {
  chat: ChatBrief;
  full: string;
  compact?: boolean;
  inverse?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const online = chat.account?.online;
  const anchor = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  // Обрезается подпись, а не отметка: в узкой шапке чата имя сотрудника остаётся на виду.
  const flex = "inline-flex min-w-0 items-baseline gap-1.5";
  if (!online)
    return (
      <span className={`${compact ? flex : ""} ${className}`} title={compact ? full : undefined} data-annot="где общались">
        <span className={compact ? "min-w-0 truncate" : ""}>{children}</span>
      </span>
    );
  const toggle = () => setOpen((was) => !was);
  return (
    <>
      <span
        ref={anchor}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        title={`${full}\n${hint(online)}`}
        // Подпись стоит внутри ссылки на чат: клик по ней открывает окно канала, а не чат.
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle();
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          e.stopPropagation();
          toggle();
        }}
        className={`${flex} ${compact ? "" : "max-w-full align-bottom"} cursor-pointer hover:underline ${className}`}
        data-annot="где общались"
      >
        <span className="min-w-0 truncate">{children}</span>
        <Mark online={online} compact={compact} inverse={inverse} />
      </span>
      {open &&
        anchor.current &&
        (online.kind === "reply" ? (
          <ReplyCard online={online} anchor={anchor.current} close={close} />
        ) : (
          <PhoneCard chat={chat} online={online} anchor={anchor.current} close={close} />
        ))}
    </>
  );
}
